import { CogIcon, HomeIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { BottomNavigationBar } from '../../components/BottomNavigationBar';
import { PageHeader } from '../../components/PageHeader';
import {
  ProjectForm,
  ProjectFormHandle,
} from '../../components/ProjectForm/ProjectForm';
import { StageNavigationBar } from '../../components/StageNavigationBar/StageNavigationBar';
import { STAGE_PAGE_WIDTH_CLASS } from '../../constants/layout';
import { BACK_BUTTON_LABEL } from '../../constants/navigation';
import {
  ARCHITECT_PERSONA,
  LINE_OF_BUSINESS_OPTIONS,
} from '../../constants/persona';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useProjectSetupState, useProjectState } from '../../hooks/useProjectState';
import { projectService } from '../../services/projectService';
import { projectStateService } from '../../services/projectStateService';
import { ProjectItem, ProjectStatus, ProjectType } from '../../types/project';

interface ProjectSetupParams extends Record<string, string | undefined> {
  projectId?: string;
}

const getPersistedProjectLineOfBusiness = (
  project: ProjectItem | null,
): string | null => project?.projectLineOfBusiness ?? project?.line_of_business ?? null;

const getDefaultProjectLineOfBusiness = (
  profile: ReturnType<typeof useUserProfile>['profile'],
): string => {
  if (profile?.persona === ARCHITECT_PERSONA) {
    return profile.line_of_business ?? '';
  }

  return '';
};

/**
 * Project Setup Page wrapper component
 * Handles both creating new projects and editing existing ones
 * Loads project data and provides navigation handlers for the Project Setup Screen
 */
export const ProjectSetupPage: React.FC = () => {
  const { projectId } = useParams<ProjectSetupParams>();
  const navigate = useNavigate();
  const projectFormRef = useRef<ProjectFormHandle>(null);
  const { profile, loading: profileLoading } = useUserProfile();
  
  const [project, setProject] = useState<ProjectItem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [selectedProjectType, setSelectedProjectType] = useState<ProjectType | ''>(
    '',
  );

  // Determine if we're in create mode (no projectId) or edit mode (has projectId)
  const isCreateMode = !projectId;
  const projectIdNum = projectId ? Number.parseInt(projectId, 10) : 0;

  // Use project state management for edit mode
  const { 
    state: setupState, 
    updateState: updateSetupState, 
    saveState: saveSetupState,
    isLoading: setupLoading, 
  } = useProjectSetupState(projectIdNum, { 
    autoSave: !isCreateMode, // Only auto-save in edit mode
    autoSaveDelay: 1500, 
  });

  const { updateProjectStatus } = useProjectState();
  const defaultProjectLineOfBusiness = getDefaultProjectLineOfBusiness(profile);

  // Load project data for edit mode
  useEffect(() => {
    if (isCreateMode) {
      // Create mode - no need to load project data
      setIsLoading(false);
      return;
    }

    const loadProject = async () => {
      try {
        setIsLoading(true);
        
        // First, try to load from state service
        try {
          const projectState = await projectStateService.getProjectState(projectIdNum);
          setProject(projectState.project);
          setError(null);
        } catch (stateError) {
          // If not in state, load from API and initialize state
          const projectData = await projectService.getProject(projectIdNum);
          setProject(projectData);
          
          // Initialize state for this project
          await projectStateService.initializeProjectState(projectData);
          setError(null);
        }
      } catch (err) {
        setError('Failed to load project data');
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [projectId, isCreateMode, projectIdNum]);

  useEffect(() => {
    if (isCreateMode) {
      setSelectedProjectType('');
      return;
    }

    setSelectedProjectType(
      (setupState?.projectType || project?.projectType || '') as ProjectType | '',
    );
  }, [isCreateMode, project?.projectType, setupState?.projectType]);

  // Handle form submission
  const handleFormSubmit = async (formData: any) => {
    if (isCreateMode) {
      // Create mode - create new project
      try {
        setIsCreating(true);
        
        // Create the project via API with all required fields
        const newProject = await projectService.createProject({
          title: formData.title,
          description: formData.description,
          projectLineOfBusiness: formData.projectLineOfBusiness,
          projectType: formData.projectType,
        });
        
        // Initialize the project state with form data
        const initialState = await projectStateService.initializeProjectState(newProject);
        
        // Update the setup state with form data
        await projectStateService.savePageState(newProject.id, 'setup', {
          title: formData.title,
          description: formData.description || '',
          projectLineOfBusiness: formData.projectLineOfBusiness ?? '',
          projectType: formData.projectType || '',
          lastModified: new Date().toISOString(),
        });

        // Advance the new project to the Context Management status so it
        // reflects the stage the user is being taken to next.
        await projectStateService.updateProjectStatus(
          newProject.id,
          ProjectStatus.CONTEXT_UPLOAD,
        );

        // Navigate to context management
        navigate(`/project/${newProject.id}/context`);
      } catch (err) {
        setError('Failed to create project');
      } finally {
        setIsCreating(false);
      }
    } else {
      // Edit mode - update existing project and state
      if (!project) {return;}

      try {
        // Update the project via API if any data changed
        const currentProjectLineOfBusiness = getPersistedProjectLineOfBusiness(project);
        const hasChanges = 
          formData.title !== project.title || 
          formData.description !== project.description ||
          formData.projectLineOfBusiness !== currentProjectLineOfBusiness;
          
        if (hasChanges) {
          await projectService.updateProject(project.id, {
            title: formData.title,
            description: formData.description,
            lineOfBusiness: formData.projectLineOfBusiness,
          });
        }

        // Update setup state
        await updateSetupState({
          title: formData.title,
          description: formData.description || '',
          projectLineOfBusiness: formData.projectLineOfBusiness ?? '',
          projectType: formData.projectType || '',
        });

        // Save immediately
        await saveSetupState();

        // Update project status if moving forward
        await updateProjectStatus(ProjectStatus.CONTEXT_UPLOAD);
        
        // Navigate to next stage
        navigate(`/project/${projectId}/context`);
      } catch (err) {
        setError('Failed to update project');
      }
    }
  };

  // Loading state
  if (isLoading || (!isCreateMode && setupLoading) || (isCreateMode && profileLoading)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0097a9] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || (!isCreateMode && !project)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Project not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-[#0097a9] text-white rounded-lg hover:bg-[#007a8a] transition-colors"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  // Get initial form data
  const initialFormData = isCreateMode ? {
    title: '',
    description: '',
    projectLineOfBusiness: defaultProjectLineOfBusiness,
    projectType: '' as const,
  } : {
    title: setupState?.title ?? project?.title ?? '',
    description: setupState?.description ?? project?.description ?? '',
    projectLineOfBusiness:
      setupState?.projectLineOfBusiness ??
      project?.projectLineOfBusiness ??
      project?.line_of_business ??
      '',
    projectType: (setupState?.projectType ?? project?.projectType ?? '') as ProjectType | '',
  };
  const isProjectTypeChangeLocked = Boolean(
    !isCreateMode &&
      project?.projectType &&
      selectedProjectType &&
      selectedProjectType !== project.projectType,
  );

  // Render the Project Setup Screen
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation - only show for edit mode */}
      {!isCreateMode && project && (
        <StageNavigationBar
          currentStage={1}
          projectId={project.id}
          isBrownfield={project?.projectType === ProjectType.BROWN_FIELD}
          projectStatus={project.status as ProjectStatus}
          onStageClick={(_stageId, route) => navigate(route)}
        />
      )}

      <div className={`${STAGE_PAGE_WIDTH_CLASS} py-8`}>
        {/* Header */}
        <PageHeader
          title={isCreateMode ? 'Create New Project' : 'Project Setup'}
          subtitle={isCreateMode 
            ? 'Initialize a new architecture project with AI assistance'
            : `Configure project details and settings for ${project?.title}`
          }
          icon={CogIcon}
          iconBgColor="#374151"
        />

        {/* Project Form */}
        <ProjectForm
          ref={projectFormRef}
          mode={isCreateMode ? 'create' : 'edit'}
          project={project || undefined}
          initialData={initialFormData}
          lineOfBusinessOptions={LINE_OF_BUSINESS_OPTIONS}
          onSubmit={handleFormSubmit}
          onCancel={() => navigate('/')}
          isLoading={isCreating}
          hideActions={true}
          onFormChange={!isCreateMode ? (formData: any) => {
            setSelectedProjectType((formData.projectType || '') as ProjectType | '');
            // Auto-save form changes in edit mode
            updateSetupState({
              title: formData.title || '',
              description: formData.description || '',
              projectType: formData.projectType || '',
            });
          } : undefined}
        />

        {/* Bottom Navigation */}
        <BottomNavigationBar
          backAction={{
            label: BACK_BUTTON_LABEL,
            onClick: () => navigate('/'),
            icon: <HomeIcon className="w-4 h-4" />,
          }}
          primaryActions={[
            {
              label: isCreateMode ? 'Create Project' : 'Update Project',
              onClick: () => projectFormRef.current?.submitForm(),
              variant: 'primary',
              disabled: isProjectTypeChangeLocked,
            },
          ]}
        />
      </div>
    </div>
  );
};
